import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getAdminInvite, acceptAdminInvite, getMe } from '../api/auth';
import styles from './AcceptInvite.module.css';

const ROLE_LABELS = {
  company_director: 'Company Director',
  super_admin: 'Super Admin',
  country_manager: 'Country Manager',
  support_agent: 'Support Agent',
};

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin-invite', token],
    queryFn: () => getAdminInvite(token).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: (payload) => acceptAdminInvite(payload).then((r) => r.data),
    onSuccess: async (result) => {
      localStorage.setItem('nula_admin_token', result.token);
      try {
        await getMe();
      } catch (_) {}
      toast.success('Invite accepted! Welcome to Nula Move Admin.');
      navigate('/applications/pending', { replace: true });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to accept invitation');
    },
  });

  useEffect(() => {
    if (data?.invite?.full_name && !fullName) {
      setFullName(data.invite.full_name);
    }
    if (data?.invite?.phone && !phone) {
      setPhone(data.invite.phone);
    }
  }, [data, fullName, phone]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!password) {
      toast.error('Please create a password');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (!fullName.trim()) {
      toast.error('Full name is required');
      return;
    }
    acceptMutation.mutate({
      token,
      password,
      full_name: fullName.trim(),
      phone: phone.trim() || undefined,
    });
  }

  const invite = data?.invite;
  const expiresAt = data?.expires_at;

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.logo}>N</div>
          <div>
            <div className={styles.brandName}>Nula Move</div>
            <div className={styles.brandSub}>Admin Staff Invitation</div>
          </div>
        </div>

        {!token && (
          <div className={styles.error}>
            <h3>Invalid invitation link</h3>
            <p>No invitation token was provided in the URL.</p>
            <button className={styles.primaryBtn} onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        )}

        {token && isLoading && (
          <div className={styles.msg}>Verifying your invitation…</div>
        )}

        {token && isError && (
          <div className={styles.error}>
            <h3>Invitation not available</h3>
            <p>
              {error?.response?.data?.error ||
                'This invitation is invalid, expired, or has already been used.'}
            </p>
            <button className={styles.primaryBtn} onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        )}

        {token && data && (
          <div>
            <div className={styles.summary}>
              <div className={styles.summaryTitle}>🎉 You're invited to the Nula Move Staff Team</div>
              {invite?.email && (
                <div className={styles.summaryRow}>
                  <span className={styles.k}>Email</span>
                  <span className={styles.v}>{invite.email}</span>
                </div>
              )}
              {invite?.role_type && (
                <div className={styles.summaryRow}>
                  <span className={styles.k}>Role</span>
                  <span className={styles.v}>{ROLE_LABELS[invite.role_type] || invite.role_type}</span>
                </div>
              )}
              {expiresAt && (
                <div className={styles.summaryRow}>
                  <span className={styles.k}>Expires</span>
                  <span className={styles.v}>{new Date(expiresAt).toLocaleString()}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <label>Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={invite?.full_name || 'Your full name'}
                />
              </div>
              <div className={styles.formRow}>
                <label>Phone</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555-0100"
                />
              </div>
              <div className={styles.formRow}>
                <label>Create Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  autoComplete="new-password"
                />
              </div>
              <div className={styles.formRow}>
                <label>Confirm Password *</label>
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
              </div>

              <div className={styles.formRow}>
                <label>Email (confirmed by your invitation)</label>
                <input
                  type="email"
                  value={invite?.email || ''}
                  disabled
                  style={{ background: '#f1f5f9', color: '#1e293b' }}
                  placeholder={invite?.email ? '' : 'Verifying invitee email…'}
                />
                {!invite?.email && (
                  <small style={{ color: '#64748b', marginTop: 4, display: 'inline-block' }}>
                    Email for this invitation will be applied automatically when you accept.
                  </small>
                )}
              </div>

              <button
                type="submit"
                className={styles.primaryBtn}
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending ? 'Setting up your account…' : 'Accept Invitation & Login'}
              </button>
            </form>

            <div className={styles.foot}>
              <span>Already have an account? </span>
              <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }} className={styles.link}>
                Sign in
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
